import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import {ContentHotel} from '@findhotel/sapi'
import {AccommodationTopBarActions} from 'components/Accommodation/AccommodationView/Desktop/AccommodationTopBarActions'
import {AiPoweredReviewsSummary} from 'components/Accommodation/AiPoweredReviews/AiPoweredReviewsSummary'
import {useAiPoweredInsight} from 'components/Accommodation/AiPoweredReviews/useAiPoweredInsight'
import {ApSeoBreadcrumb} from 'components/Accommodation/ApSeoBreadcrumb/ApSeoBreadcrumb'
import {ApTopHotelsLinks} from 'components/Accommodation/ApTopHotelsLinks/ApTopHotelsLinks'
import {GoBackToSearchButton} from 'components/Accommodation/BackToSearchButton'
import {useSrpQueryString} from 'components/Accommodation/BackToSearchButton/helpers'
import {DealDetailsOverlay} from 'components/Accommodation/DealDetails'
import {FacilitiesPopup} from 'components/Accommodation/Facilities/FacilitiesPopup'
import {PopularAmenities} from 'components/Accommodation/Facilities/PopularAmenities'
import {GuestReviewsOverlayPage} from 'components/Accommodation/GuestReviewsOverlay'
import {useResumableDealFreezeRedirect} from 'components/Accommodation/hooks/useResumableDealFreezeRedirect'
import {useScrollToRoomFromUrlParams} from 'components/Accommodation/hooks/useScrollToRoomFromUrlParams'
import {HotelDescriptions} from 'components/Accommodation/HotelDescriptions/HotelDescriptions'
import {HotelGalleryOverlay} from 'components/Accommodation/HotelGalleryOverlay'
import {HotelOverview} from 'components/Accommodation/HotelOverview'
import {AskHotelQuestion} from 'components/Accommodation/HotelQuestionModal'
import {LocationOverlayDesktop} from 'components/Accommodation/LocationOverlay'
import {NetworkStatusToast} from 'components/Accommodation/NetworkStatusToast'
import {BottomCTA} from 'components/Accommodation/Offers/BottomCTA'
import {DatelessOfferBoxV2} from 'components/Accommodation/Offers/DatelessOfferBoxV2/DatelessOfferBoxV2'
import {DesktopAPClickerCTA} from 'components/Accommodation/Offers/DesktopAPClickerCTA'
import {OfferBoxDesktopContainer} from 'components/Accommodation/Offers/OfferBoxDesktopContainer/OfferBoxDesktopContainer'
import {PrimaryRoomOffer} from 'components/Accommodation/Offers/PrimaryRoomOffer'
import {TopOffersDesktopRedesigned} from 'components/Accommodation/Offers/TopOffers/Desktop/TopOffersDesktopRedesigned'
import {useAccommodationTopOffers} from 'components/Accommodation/Offers/useAccommodationTopOffers'
import {PoliciesDetailsOverlay} from 'components/Accommodation/Policies/PoliciesDetailsOverlay'
import {BookOnAppConfirmation} from 'components/Accommodation/Rooms/BookOnAppConfirmation'
import {OfferCheckLoadingOverlay} from 'components/Accommodation/Rooms/OfferCheckLoadingOverlay'
import {RoomsAlternativeOffers} from 'components/Accommodation/Rooms/RoomsAlternativeOffers'
import {DatelessRoomSection} from 'components/Accommodation/Rooms/RoomsSection/DatelessRoomSection'
import {RoomsSection} from 'components/Accommodation/Rooms/RoomsSection/RoomsSection'
import {RoomsDetailOverlayContainer} from 'components/Accommodation/RoomsDetailOverlayContainer'
import {AccommodationViewSuggestedHotels} from 'components/Accommodation/SuggestedHotels'
import {CallToBook} from 'components/CallToBook'
import {CompactFooter} from 'components/CompactFooter'
import {generateNavigationUrl} from 'components/data/WithUrlUpdater'
import {ExitPopupAccommodationPage} from 'components/ExitPopup'
import {Footer} from 'components/Footer'
import {LoginBanner} from 'components/LoginBanner'
import {BackToTopButton} from 'components/Pagination/BackToTopButton'
import {PriceAlertPrompt} from 'components/PriceAlertPrompt'
import {useArePriceInsightsAvailable} from 'components/PriceTrend/hooks/useArePriceInsightsAvailable'
import {PriceTrendDesktop} from 'components/PriceTrend/PriceTrendDesktop'
import {PriceWatchCTAWrapper} from 'components/PriceWatchCTAWrapper/PriceWatchCTAWrapper'
import {ScrollSection, scrollToSection} from 'components/ScrollSpy'
import {SEARCH_ROUTE} from 'constants/index'
import {OfferTrackingContextProvider} from 'context/offerTracking'
import {OfferViewContext, OfferViewType} from 'context/offerViewContext'
import {useAccommodation} from 'hooks/useAccommodation'
import {useAccommodationPageScrollTopOffset} from 'hooks/useAccommodationPageScrollTopOffset'
import {useGetFaqsAvailibility} from 'hooks/useGetFaqsAvailibility'
import {useHotelSlug} from 'hooks/useHotelSlug'
import {useShouldShowLoginBanner} from 'hooks/useShouldShowLoginBanner'
import {trackAccommodationPageReady} from 'middleware/analytics/actions/trackEvent'
import {
  getIsLightboxOverlayVisible,
  getIsVioOfferOrBundleSelected,
  getMetaBrandHasRooms
} from 'modules/accommodation/selectors'
import {
  setLightboxOverlayVisibility,
  setLightboxSelectedImage
} from 'modules/accommodation/slice'
import {AccommodationPageNavSections} from 'modules/accommodation/types'
import {
  getIsNoDatesSearch,
  getSearchDesktopHeaderHeight
} from 'modules/search/selectors'
import {
  getHotelRoomsData,
  getIsFilteredRoomsResultEmpty,
  getIsRoomsQueryLoading,
  getIsRoomsResultEmpty
} from 'modules/searchApi/selectors'

import {ButtonLink} from '@vio/atlas/ButtonLink'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {PageWidthContainer} from '@vio/atlas/helpers/PageWidthContainer'
import {StarRating} from '@vio/atlas/StarRating'
import {Text} from '@vio/atlas/Text'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {scrollToElement} from '@vio/core/src/_web/utils/browser'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {PriceWatchToggle} from '@vio/shared/src/priceWatch/components/PriceWatchToggle/PriceWatchToggle'
import {ReviewRating} from '@vio/shared/src/search/ReviewRating'
import {Breadcrumbs} from '@vio/shared/src/whiteLabel/Breadcrumbs/Breadcrumbs'
import {usePointsPriceDisplay} from '@vio/shared/src/whiteLabel/hooks/usePointsPriceDisplay'
import {PointsPriceToggle} from '@vio/shared/src/whiteLabel/PointsPriceToggle'
import {PoweredBy} from '@vio/shared/src/whiteLabel/PoweredBy/PoweredBy'

import {useOtaMode} from '../../../../hooks/useOtaMode'
import {ReviewSummary as ReviewSummaryRedesigned} from '../../../Reviews/ReviewSummary'
import {AccommodationFixedHeader} from '../../AccommodationFixedHeader/AccommodationFixedHeader'
import {GridGallery} from '../../GridGallery'
import {HotelDetails} from '../../HotelDetails'
import {HotelLocationDetails} from '../../HotelLocationDetails/HotelLocationDetails'
import {LocationSummary} from '../../LocationSummary'
import {PoliciesSection} from '../../PoliciesSection'
import {PopularFacilities} from '../../PopularFacilities'
import {SelectedElementType} from '../../ReviewsSection'
import {ReviewsSection} from '../../ReviewsSection'
import {getReviewAvailability} from '../../ReviewsSection/utils'
import {ReviewSummary} from '../../ReviewSummary'
import {ConditionalFAQ} from '../Mobile/ConditionallyRenderedSections/ConditionalFAQ'
import {ConditionalPriceInfoSection} from '../Mobile/ConditionallyRenderedSections/ConditionalPriceInfoSection'
import {HotelAccommodationDesktopViewLoading} from './HotelAccommodationDesktopViewLoading'
import {HotelTitleBlock} from './HotelTitleBlock'
import {
  DividerContainer,
  DividerContainer700,
  FooterWrapper,
  GridGalleryWrapper,
  HotelDetailsWrapper,
  LeftContentWrapper,
  MainContentWrapper,
  PriceWatchWrapper,
  ReviewSummaryWrapper,
  RightContentWrapper,
  RoomsSectionWrapper,
  Wrapper
} from './styles'
import {TopOfferCtaDesktop} from './TopOfferCtaDesktop'
import {useShouldShowHotelCardClickersContent} from './useShouldShowHotelCardClickersContent'

const OFFER_BOX_DATA_ID = 'offerBox'

const handleScrollTop = () => {
  window.scrollTo({top: 0, behavior: 'smooth'})
}

interface Props {
  hotel: ContentHotel
  isLoading: boolean
}

export const HotelAccommodationDesktopView: React.FC<Props> = ({
  hotel,
  isLoading
}) => {
  const shouldShowHotelCardClickersContent =
    useShouldShowHotelCardClickersContent()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const {objectID: hotelId} = hotel || {}
  const {isDesktopMd, isDesktopLg} = useDeviceLayout()
  const isLargeDesktop = isDesktopLg || isDesktopMd
  const isNoDatesSearch = useSelector(getIsNoDatesSearch)

  const searchDesktopHeaderHeight = useSelector(getSearchDesktopHeaderHeight)
  const brandHasRooms = useSelector(getMetaBrandHasRooms)
  const {isOTAMode} = useOtaMode()
  const {isAvailable: hasPointsToggle} = usePointsPriceDisplay()
  const {shouldRenderFaqs} = useGetFaqsAvailibility({hotelId})

  const galleryViewRootMargin = isLargeDesktop
    ? `-${searchDesktopHeaderHeight}px 0px 0px 0px`
    : '0px'

  const scrollOffset = useAccommodationPageScrollTopOffset()
  const topOffersRef = useRef<HTMLDivElement | null>(null)
  const scrollToTopOffers = useCallback(() => {
    if (isOTAMode) {
      scrollToSection(AccommodationPageNavSections.Rooms)
      return
    }
    scrollToElement(topOffersRef.current, 800, scrollOffset)
  }, [isOTAMode, scrollOffset])

  const onViewDealsClick = useCallback(() => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.HeaderCTA,
        action: Action.Clicked
      })
    )
    scrollToTopOffers()
  }, [dispatchTeamEvent, scrollToTopOffers])

  const [hasSimilarHotels, setHasSimilarHotels] = useState(!isNoDatesSearch)
  const dispatch = useDispatch()
  const reviewsRef = useRef<HTMLDivElement>()
  const rightContentRef = useRef<HTMLDivElement>()
  const {ref: galleryRef, inView: isGalleryInView} = useInView({
    threshold: 0,
    initialInView: true,
    rootMargin: galleryViewRootMargin
  })

  // Sentinel for the B-side inline nav: when it's in view we render the nav
  // inline below the gallery; when it scrolls out of view we swap to sticky.
  const {ref: inlineNavSentinelRef, inView: isInlineNavSentinelInView} =
    useInView({
      threshold: 0,
      initialInView: true
    })

  const {ref: roomsSectionRef, entry: roomsIntersectionObserver} = useInView({
    threshold: 0
  })

  const {ref: overviewSectionCallbackRef, entry: overviewSectionObserver} =
    useInView({threshold: 0})

  const topOffersAndOverviewRef = useCallback(
    (node: HTMLDivElement | null) => {
      topOffersRef.current = node
      overviewSectionCallbackRef(node)
    },
    [overviewSectionCallbackRef]
  )

  const {
    ref: aiPoweredReviewsSummaryRef,
    inView: isAiPoweredReviewsSummaryInView
  } = useInView({
    threshold: 0.5,
    triggerOnce: true
  })

  // Show price insights only when API returns valid data and hotel has offers
  // Hook internally determines offer type and applies filter-based hiding only for BoVio offers
  const {isEnabled: isPriceTrendsEnabled} = useArePriceInsightsAvailable({
    hotelId
  })
  const hasPriceTrend = !isNoDatesSearch && isPriceTrendsEnabled

  useEffect(() => {
    if (isAiPoweredReviewsSummaryInView) {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.AiReviewSummarySection,
          action: Action.Viewed,
          payload: {
            component: 'ImageGallery'
          }
        })
      )
    }
  }, [isAiPoweredReviewsSummaryInView, dispatchTeamEvent])

  const hasScrollPassedRoomSection =
    roomsIntersectionObserver?.boundingClientRect?.bottom < 0

  const hasScrollPassedOverviewSection =
    overviewSectionObserver?.boundingClientRect?.bottom < 0

  const {
    hasPopularFacilitiesSection,
    hasFacilitiesSection,
    hasPolicies,
    topFacilities
  } = useAccommodation(hotel)

  const isRoomsLoading = useSelector(getIsRoomsQueryLoading)
  const {offerEntity, isComplete, searchedParameters, hasAnyOffers} =
    useAccommodationTopOffers({hotelId})

  const hasNoFilteredRooms = useSelector(getIsFilteredRoomsResultEmpty)
  const hasNoRooms = useSelector(getIsRoomsResultEmpty)
  const datelessRooms = useSelector(state => getHotelRoomsData(state, hotelId))
  const {brand} = useBrand()
  const {
    monetisation: {cashback},
    commerce: {coBranded},
    shell: {hasFullBrandFooter}
  } = useBrandCapabilities()
  const isCoBranded = coBranded.enabled

  const showLoginBanner = useShouldShowLoginBanner()

  // OTA mode renders its own AccommodationOfferBox layout (OtaOffer),
  // so it should not fall through to the standalone-AP TopOffersDesktop layout.
  const isRedirectToStandaloneAP = !isOTAMode

  // Hide rooms section when sold out on AP experiment or hotel clicker,
  // as those flows handle the sold-out UI separately

  const hasRoomSection = !isNoDatesSearch && brandHasRooms && !hasNoRooms
  const hasDatelessRoomSection =
    isNoDatesSearch && brandHasRooms && Boolean(datelessRooms.length)

  const isVioOfferSelected = useSelector(getIsVioOfferOrBundleSelected)

  const {hasReviews} = getReviewAvailability(hotel)

  const isLightboxOverlayVisible = useSelector(getIsLightboxOverlayVisible)

  const handleLocationSummaryClick = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.HotelDetailsLocationSummary,
        action: Action.Clicked
      })
    )
    // Scroll down to hotel location
    scrollToSection(AccommodationPageNavSections.HotelLocation)
  }
  const handleReviewSummaryClick = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.HotelDetailsReviewSummary,
        action: Action.Clicked,
        payload: {
          selectedElement: SelectedElementType.Rating
        }
      })
    )

    if (hasReviews) {
      // Scroll to review section
      scrollToSection(AccommodationPageNavSections.Reviews)
    }
  }

  const onLightboxGalleryClose = useCallback(() => {
    dispatch(setLightboxOverlayVisibility(false))
    dispatch(setLightboxSelectedImage(undefined))
  }, [dispatch])

  const onGalleryOverlayGuestReviewsClick = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.HotelDetailsReviewSummary,
        action: Action.Clicked,
        payload: {
          component: 'ImageGallery'
        }
      })
    )

    onLightboxGalleryClose()
    scrollToSection(AccommodationPageNavSections.Reviews)
  }

  useEffect(() => {
    dispatch(trackAccommodationPageReady())
  }, [dispatch])

  useResumableDealFreezeRedirect({
    hotelId,
    onResume: () => scrollToSection(AccommodationPageNavSections.Rooms)
  })

  useScrollToRoomFromUrlParams({hotelId})

  const {isEnabled: isAiPoweredReviewInsightsEnabled} =
    useAiPoweredInsight(hotelId)

  // Generate breadcrumb data
  const hotelSlug = useHotelSlug()
  const srpQueryParams = useSrpQueryString(hotelSlug)

  const placeOfSearchUrl = !hotel?.placeDisplayName
    ? undefined
    : generateNavigationUrl(srpQueryParams, {basePath: SEARCH_ROUTE})

  const placeOfSearch =
    hotel?.placeDisplayName && placeOfSearchUrl
      ? {
          label: hotel.placeDisplayName,
          href: placeOfSearchUrl
        }
      : undefined

  const hotelName = hotel?.hotelName
    ? {
        label: hotel.hotelName
      }
    : undefined

  // Show skeletons in case we dropped into CSR
  if (isLoading || !hotel) return <HotelAccommodationDesktopViewLoading />

  // For Search Whitelabels (brandHasRooms = false), don't consider rooms loading state
  // since we're not using rooms anyway
  const isOffersOrRoomsLoading =
    !isComplete || (brandHasRooms && isRoomsLoading)

  const hasRoomsAndScrollPassedRoomSection =
    !hasNoRooms && hasScrollPassedRoomSection

  // B-side sticky: show CTA when at least one top offer is available, or on a Dateless AP.
  // A-side keeps today's rooms-based scroll gating.
  const isCtaVisible =
    shouldShowHotelCardClickersContent && !isOTAMode
      ? (hasAnyOffers || isNoDatesSearch) && hasScrollPassedOverviewSection
      : hasRoomsAndScrollPassedRoomSection

  // B-side: Sentinel starts in view (initialInView: true) so
  // inline is the default before the first scroll.
  const shouldRenderInlineHeader = isInlineNavSentinelInView

  const isStickyHeaderVisible = shouldShowHotelCardClickersContent
    ? !shouldRenderInlineHeader
    : !isGalleryInView

  return (
    <OfferViewContext.Provider value={OfferViewType.AP_DESKTOP}>
      <NetworkStatusToast />
      <AccommodationFixedHeader
        isVisible={isStickyHeaderVisible && !process.env.STORYBOOK}
        isCtaVisible={isCtaVisible}
        hasReviews={hasReviews}
        hasRooms={hasRoomSection || hasDatelessRoomSection}
        hasFacilities={hasFacilitiesSection}
        hasPolicies={hasPolicies}
        isLoading={!isNoDatesSearch && isOffersOrRoomsLoading}
        isPriceTrendsEnabled={hasPriceTrend}
        hasFAQs={shouldRenderFaqs}
      />
      <Wrapper>
        <PageWidthContainer paddingDesktopXsSize="s500">
          <ScrollSection name={AccommodationPageNavSections.Photos}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom="s500"
            >
              <Box display="flex" alignItems="center" gap="s500">
                <GoBackToSearchButton hideText={isCoBranded} />
                {isCoBranded && (
                  <Breadcrumbs
                    placeOfSearch={placeOfSearch}
                    hotelName={hotelName}
                  />
                )}
              </Box>
              <Box display="flex" alignItems="center" gap="s500">
                <AccommodationTopBarActions
                  hotel={hotel}
                  searchIsCompleted={!isOffersOrRoomsLoading}
                />
                {isCoBranded && <PoweredBy />}
              </Box>
            </Box>
            {shouldShowHotelCardClickersContent && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                gap="s500"
                marginBottom="s500"
              >
                <HotelTitleBlock
                  hotel={hotel}
                  onReviewSummaryClick={handleReviewSummaryClick}
                />
                <TopOfferCtaDesktop
                  hotelId={hotelId}
                  onViewDealsClick={onViewDealsClick}
                />
              </Box>
            )}
            <GridGalleryWrapper
              ref={galleryRef}
              id="photos"
              noBottomMargin={shouldShowHotelCardClickersContent}
            >
              <GridGallery
                images={hotel.images || []}
                hotelName={hotel.hotelName}
              />
            </GridGalleryWrapper>
          </ScrollSection>
          {shouldShowHotelCardClickersContent && (
            <div ref={inlineNavSentinelRef}>
              <AccommodationFixedHeader
                mode="inline"
                isVisible={shouldRenderInlineHeader && !process.env.STORYBOOK}
                hasReviews={hasReviews}
                hasRooms={hasRoomSection || hasDatelessRoomSection}
                hasFacilities={hasFacilitiesSection}
                hasPolicies={hasPolicies}
                isLoading={isOffersOrRoomsLoading}
                isPriceTrendsEnabled={hasPriceTrend}
                hasFAQs={shouldRenderFaqs}
              />
            </div>
          )}
          {shouldShowHotelCardClickersContent ? (
            <>
              <ScrollSection
                name={AccommodationPageNavSections.Overview}
                scrollTopMargin={scrollOffset}
              >
                <div ref={topOffersAndOverviewRef}>
                  {isNoDatesSearch && hotel ? (
                    <Box marginTop="s600">
                      <DatelessOfferBoxV2
                        variant="horizontal"
                        hotelId={hotel.objectID}
                        hotelName={hotel.hotelName}
                        priceFrom={hotel.pricing?.minRateInUserCurrency}
                      />
                    </Box>
                  ) : (
                    // OTA mode only returns the brand's own offers, so the
                    // rooms list already shows every deal.
                    !isOTAMode && (
                      <Box marginTop="s600">
                        <TopOffersDesktopRedesigned />
                      </Box>
                    )
                  )}
                </div>
                {showLoginBanner && (
                  <Box paddingTop="s500">
                    <LoginBanner isRowLayout />
                  </Box>
                )}
              </ScrollSection>
              <Box paddingTop="s900" paddingBottom="s900">
                <HotelOverview hotel={hotel} />
              </Box>
            </>
          ) : (
            <MainContentWrapper>
              <LeftContentWrapper>
                <HotelDetailsWrapper>
                  <HotelDetails hotel={hotel} />
                </HotelDetailsWrapper>
                <LocationSummary
                  hotel={hotel}
                  onLocationSummaryClick={handleLocationSummaryClick}
                />

                <Box paddingTop="s200">
                  <CallToBook
                    sourceComponent="AccommodationDesktopView"
                    variant="AP"
                  />
                </Box>

                <ReviewSummaryWrapper>
                  <ReviewSummary
                    hotel={hotel}
                    onReviewSummaryClick={handleReviewSummaryClick}
                  />
                </ReviewSummaryWrapper>
                {hasPopularFacilitiesSection && (
                  <>
                    <DividerContainer700 />
                    <ScrollSection
                      name={AccommodationPageNavSections.Amenities}
                      scrollTopMargin={scrollOffset}
                    >
                      <PopularFacilities hotel={hotel} />
                    </ScrollSection>
                  </>
                )}

                <HotelDescriptions hotel={hotel} />
                {hasPriceTrend ? (
                  <>
                    <ScrollSection
                      name={AccommodationPageNavSections.PriceTrend}
                      scrollTopMargin={scrollOffset}
                    >
                      <PriceTrendDesktop
                        key={hotelId}
                        hotelId={hotelId}
                        areOffersLoaded={isComplete}
                        onPriceTrendButtonClick={() => {
                          scrollToSection(AccommodationPageNavSections.Rooms, {
                            deferred: true
                          })
                        }}
                      />
                    </ScrollSection>
                  </>
                ) : (
                  <Box paddingTop="s900" />
                )}
              </LeftContentWrapper>
              <RightContentWrapper
                ref={rightContentRef}
                style={
                  {
                    '--scroll-top-margin': `${scrollOffset}px`
                  } as React.CSSProperties
                }
              >
                {!isNoDatesSearch && (
                  <PriceWatchWrapper
                    className={cx(hasPointsToggle && '--before-points-toggle')}
                  >
                    <PriceWatchCTAWrapper
                      hotel={hotel}
                      ctaComponent={PriceWatchToggle}
                      componentName="PriceWatchToggle"
                      searchIsCompleted={!isOffersOrRoomsLoading}
                    />
                  </PriceWatchWrapper>
                )}
                {hasPointsToggle && (
                  <Box marginBottom="s400">
                    <PointsPriceToggle asRow id="offerBoxPointsPriceToggle" />
                  </Box>
                )}
                <OfferTrackingContextProvider
                  offerEntity={offerEntity}
                  parameters={searchedParameters}
                >
                  <div data-id={OFFER_BOX_DATA_ID}>
                    <OfferBoxDesktopContainer
                      hotelId={hotelId}
                      offerEntity={offerEntity}
                      isVioOfferSelected={isVioOfferSelected}
                      isRedirectToStandaloneAP={isRedirectToStandaloneAP}
                      isOffersOrRoomsLoading={isOffersOrRoomsLoading}
                      cashback={cashback}
                    />
                  </div>
                </OfferTrackingContextProvider>
                <Box paddingTop={showLoginBanner ? 's500' : 's900'} />
                {showLoginBanner && (
                  <Box paddingBottom="s900">
                    <LoginBanner />
                  </Box>
                )}
              </RightContentWrapper>
            </MainContentWrapper>
          )}
        </PageWidthContainer>
        {hasDatelessRoomSection && (
          <>
            <Divider />
            <RoomsSectionWrapper ref={roomsSectionRef}>
              <PageWidthContainer
                paddingDesktopXsSize="s500"
                maxPageWidth={1154}
              >
                <DatelessRoomSection hotelId={hotelId} />
              </PageWidthContainer>
            </RoomsSectionWrapper>
          </>
        )}
        {hasRoomSection && (
          <>
            <Divider />
            <RoomsSectionWrapper ref={roomsSectionRef}>
              <PageWidthContainer
                paddingDesktopXsSize="s500"
                maxPageWidth={1154}
              >
                <RoomsSection hotelId={hotelId} />
              </PageWidthContainer>
            </RoomsSectionWrapper>
          </>
        )}
        <Box paddingBottom="s900">
          <Divider />
        </Box>
        <PageWidthContainer paddingDesktopXsSize="s500">
          {hasReviews && (
            <>
              <ScrollSection
                name={AccommodationPageNavSections.Reviews}
                scrollTopMargin={scrollOffset}
              >
                <ReviewsSection
                  anchorRef={reviewsRef}
                  hotel={hotel}
                  isFullLayout
                />
              </ScrollSection>
              <DividerContainer />
            </>
          )}
          {!shouldShowHotelCardClickersContent && (
            <ScrollSection
              name={AccommodationPageNavSections.HotelLocation}
              scrollTopMargin={scrollOffset}
            >
              <HotelLocationDetails hotel={hotel} />
              <DividerContainer />
            </ScrollSection>
          )}

          {hasPolicies && (
            <>
              <ScrollSection
                name={AccommodationPageNavSections.Policies}
                scrollTopMargin={scrollOffset}
              >
                <PoliciesSection hotel={hotel} />
              </ScrollSection>
            </>
          )}
          <ConditionalFAQ hotelId={hotelId} scrollTopMargin={scrollOffset} />
        </PageWidthContainer>
        <AskHotelQuestion hotelName={hotel?.hotelName} />
        <PageWidthContainer paddingDesktopXsSize="s500">
          <ConditionalPriceInfoSection hotel={hotel} variant="horizontal" />
        </PageWidthContainer>
        {hasSimilarHotels && !hasNoFilteredRooms && (
          <Box paddingTop="s600" paddingBottom="s900">
            <AccommodationViewSuggestedHotels
              onHotelsLoadingComplete={setHasSimilarHotels}
              hotelId={hotelId}
              hasPadding
              isLazyLoaded
            />
          </Box>
        )}
        <ApSeoBreadcrumb />
        <BackToTopButton enabled onClick={handleScrollTop} />
      </Wrapper>
      <FooterWrapper>
        <>
          {hasFullBrandFooter ? (
            <Footer brand={brand} linksSlot={<ApTopHotelsLinks />} />
          ) : (
            <CompactFooter forcedLayout="wide" />
          )}
        </>
      </FooterWrapper>

      <HotelGalleryOverlay
        images={hotel?.images}
        isVisible={isLightboxOverlayVisible}
        onClose={onLightboxGalleryClose}
        hotelDetails={
          <Box
            paddingTop="s400"
            paddingBottom="s400"
            paddingLeft="s500"
            paddingRight="s500"
            display="flex"
            flexDirection="column"
            gap="s800"
          >
            <div>
              <StarRating
                size="xs"
                data-id="StarRating"
                rating={hotel.starRating}
                isInline
              />
              <Text variant="titleL">{hotel.hotelName}</Text>
              <Box marginTop="s300">
                <ReviewRating
                  rating={hotel.guestRating.overall}
                  totalRatingCount={hotel.reviewCount}
                  dataId="ReviewRatingSummary"
                  hotelId={hotel.objectID}
                />
              </Box>
              {isAiPoweredReviewInsightsEnabled ? (
                <Box paddingTop="s500">
                  <div ref={aiPoweredReviewsSummaryRef}>
                    <AiPoweredReviewsSummary
                      hotelId={hotelId}
                      isCompact
                      onReadMoreClick={onGalleryOverlayGuestReviewsClick}
                    />
                  </div>
                </Box>
              ) : (
                <>
                  <Box paddingTop="s600">
                    <ReviewSummaryRedesigned guestRating={hotel.guestRating} />
                  </Box>
                  {hasReviews && (
                    <Box paddingTop="s500">
                      <ButtonLink
                        isCompact
                        onClick={onGalleryOverlayGuestReviewsClick}
                      >
                        <FormattedMessageWrapper
                          id="reviews.readGuestReviews"
                          defaultMessage="Read guest reviews"
                        />
                      </ButtonLink>
                    </Box>
                  )}
                </>
              )}
            </div>
            <PopularAmenities facilities={topFacilities} />
          </Box>
        }
        primaryRoomOffer={
          !hasNoFilteredRooms &&
          (shouldShowHotelCardClickersContent ? (
            <div data-id="StaticOffer">
              <DesktopAPClickerCTA
                hotelId={hotelId}
                component="DesktopGallery"
                sourceComponent="DesktopGallery"
              />
            </div>
          ) : (
            <div data-id="StaticOffer">
              <PrimaryRoomOffer hotelId={hotelId} component="DesktopGallery" />
            </div>
          ))
        }
        bottomCTA={
          <BottomCTA hotelId={hotelId} sourceComponent="DesktopGallery" />
        }
      />

      <LocationOverlayDesktop
        hotel={hotel}
        isLoading={isOffersOrRoomsLoading}
        hasOffer={!hasNoFilteredRooms}
      />
      <RoomsDetailOverlayContainer />
      <DealDetailsOverlay />
      <PoliciesDetailsOverlay hotelId={hotelId} />
      <FacilitiesPopup hotel={hotel} />
      <GuestReviewsOverlayPage hotel={hotel} />
      {!isNoDatesSearch && (
        <ExitPopupAccommodationPage
          hotel={hotel}
          offerEntity={offerEntity}
          isLoading={!hotel || !isComplete}
        />
      )}
      <PriceAlertPrompt key={hotelId} hotel={hotel} />
      <OfferCheckLoadingOverlay />
      <RoomsAlternativeOffers hotel={hotel} />
      <BookOnAppConfirmation hotel={hotel} />
    </OfferViewContext.Provider>
  )
}
